import AutocompleteTag from 'components/AutocompleteTag';
import CircularLoader from 'components/CircularLoader';
import PersonOptionLabel from 'components/employee/PersonOptionLabel';
import { useInRecordSection, useSectionWideClass } from 'components/section-edit/SectionEdit';
import {
    Combobox,
    ComboboxChip,
    ComboboxChips,
    ComboboxChipsInput,
    ComboboxClear,
    ComboboxCollection,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxGroup,
    ComboboxItem,
    ComboboxLabel,
    ComboboxList,
    ComboboxTrigger,
    useComboboxAnchor,
} from 'components/ui/combobox';
import { InputGroupButton } from 'components/ui/input-group';
import { useInfiniteScroll } from 'hooks/useInfiniteScroll';
import get from 'lodash/get';
import React, { ReactNode, useMemo, useRef, useState } from 'react';
import { Control, Controller, FieldValues, SetFieldValue, useWatch } from 'react-hook-form';
import { groupConsecutiveOptions, OptionGroup } from 'utils/autocompleteGrouping';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { optionDisplayLabel, useLastNameFirst } from 'utils/nameOrder';
import { useSameNameEmail } from 'utils/sameNameEmail';
import ReadonlyField from '../rhf-other/ReadonlyField';
import { CommonInputProps, DeepKeyByValue, KeyByValue } from './InputTypes';
import InputWrapper from './InputWrapper';
import { useAdornmentGetters } from './useAdornmentGetters';
import { useOwnPlaceholder } from './newDesignInput';
import { useMemoizedRules } from './useMemoizedRules';
import { PaginatedListProps, usePaginatedPopup } from './usePaginatedList';

type GetOptionIdentifier<TOption extends FieldValues> =
    | ((option: TOption) => string | number)
    | KeyByValue<TOption, number | string>;
type GetOptionLabel<TOption extends FieldValues> =
    | ((option: TOption) => string)
    | KeyByValue<TOption, number | string | null>;

type GetOptionEndAdornment<TOption extends FieldValues> = ((option: TOption) => ReactNode) | keyof TOption;

type GetOptionStartAdornment<TOption extends FieldValues> = ((option: TOption) => ReactNode) | keyof TOption;

type CustomOptionRenderer<TOption extends FieldValues> = (
    option: TOption,
    /**
     * Always empty. The row's own props — role, click, highlight — belong to the combobox item
     * this element is rendered inside, so there is nothing left to hand a renderer. The parameter
     * stays in the signature because every renderer already takes it positionally.
     */
    props: React.HTMLAttributes<HTMLLIElement>,
    getValue: (option: TOption) => string | number | null
) => { element: ReactNode; isDisabled: boolean };

/** Why the selection changed, in the vocabulary the call sites branch on. */
export type MultiSelectChangeReason = 'createOption' | 'selectOption' | 'removeOption' | 'clear' | 'blur';
/** Which option a `selectOption`/`removeOption` change was about. */
export type MultiSelectChangeDetails<TOption> = { option: TOption };
/** What a tag renderer is handed per selected value: the props an `AutocompleteTag` expects. */
export type GetTagProps = (params: { index: number }) => {
    key: string | number;
    disabled?: boolean;
    section?: boolean;
    onDelete: (event: React.SyntheticEvent) => void;
};

// In a record section the typing input takes no room until it is typed in, so an empty one never wraps onto a line
// of its own and the chips row stays as tall as the read view's. After a chip its placeholder is not drawn, as it
// would be cut to whatever the row has left; it stays set, being what tells an empty input from a typed-in one.
export const SECTION_CHIPS_INPUT_CLASS =
    'min-w-0 not-placeholder-shown:min-w-16 [[data-slot=combobox-chip]~&]:placeholder:text-transparent';

/**
 * In a record section a multi-select's label leaves the end of its line free: a picker has its button there, 24px
 * and a 4px gap. The read view's chips row leaves the same room, so a label wraps alike in both.
 */
export const SECTION_MULTI_SELECT_CLASS = '[&>div:first-child]:pr-7';

type RenderTagsFunction<TOption extends FieldValues> = (value: TOption[], getTagProps: GetTagProps) => ReactNode;

export interface Props<TFormData extends FieldValues, TOption extends FieldValues> extends CommonInputProps<TFormData> {
    placeholderOnFocus?: string;
    clearable?: boolean;
    onChange?: (
        value: TOption[] | null,
        reason?: MultiSelectChangeReason,
        details?: MultiSelectChangeDetails<TOption> | undefined
    ) => void;
    onSearchChange?: (eventType: string, searchValue: string) => void;
    getOptionLabel: GetOptionLabel<TOption>;
    setValue: SetFieldValue<TFormData>;
    control: Control<TFormData>;
    getOptionIdentifier: GetOptionIdentifier<TOption>;
    getOptionEndAdornment?: GetOptionEndAdornment<TOption>;
    getOptionStartAdornment?: GetOptionStartAdornment<TOption>;
    customOptionRenderer?: CustomOptionRenderer<TOption>;
    renderTags?: RenderTagsFunction<TOption>;
    /** Rendered before the label inside each selected chip, never on the list rows. Unused when `renderTags` is given. */
    getSelectedValueStartAdornment?: (option: TOption) => ReactNode;
    /** Rendered after the label inside each selected chip, never on the list rows. Unused when `renderTags` is given. */
    getSelectedValueEndAdornment?: (option: TOption) => ReactNode;
    groupSortOrder?: string[]; // legacy, does nothing but allows to preserve already set sort orders that could potentially be made functional again
    /**
     * Ensure that the value specified in `groupByField` is also included in the `fetch` sort options (`paginated.sort`)
     * with the same value to avoid duplicate grouping issues.
     */
    groupByField?: ((option: TOption) => string | number) | DeepKeyByValue<TOption, number | string>;
    /**
     * Function to group options by static groups. Takes precedence over groupByField.
     * Return null to disable grouping for specific options.
     */
    groupByStaticFields?: (option: TOption) => string | null;
    defaultGroup?: string;
    noOptionsContent?: React.ReactNode;
    readonly?: boolean | { linkTo: string };
    paginated: PaginatedListProps<TOption>;
    /** Lists the picked options ticked, in place, instead of taking them out of the list. */
    keepSelected?: boolean;
    /** Classes for the list, e.g. a shorter fixed height that fits under the field where the default flips it above. */
    listClassName?: string;
}

const DynamicMultiSelectInput = <TFormData extends FieldValues, TOption extends FieldValues>({
    name,
    label,
    shouldTranslateLabel = true,
    required,
    tooltip,
    rightLabelComponent,
    bottomRightLabelComponent,
    disabled,
    placeholder,
    shouldTranslatePlaceholder = true,
    validate,
    placeholderOnFocus,
    wrapperClassName,
    control,
    clearable,
    onChange,
    onSearchChange,
    getOptionLabel,
    getOptionIdentifier,
    getOptionEndAdornment,
    getOptionStartAdornment,
    customOptionRenderer,
    renderTags,
    getSelectedValueStartAdornment,
    getSelectedValueEndAdornment,
    groupByField,
    groupByStaticFields,
    defaultGroup,
    noOptionsContent,
    readonly = false,
    paginated,
    keepSelected = false,
    listClassName,
}: Props<TFormData, TOption>) => {
    const rules = useMemoizedRules({ required, validate, requiredMessageId: 'validation.choose-option' });
    const watchedValue = useWatch({ control, name });
    // A record section in edit draws the values as its read view does: the whole row for stored values and one
    // cell without, in chips whose remove button takes no room, so a chip is as wide in both.
    const inSection = useInRecordSection();
    const wideClass = useSectionWideClass(control, name);
    const isEmpty =
        watchedValue === undefined ||
        watchedValue === null ||
        (Array.isArray(watchedValue) && watchedValue.length === 0);
    const initialPlaceholder = useOwnPlaceholder(placeholder, label, shouldTranslatePlaceholder) ?? t('select');
    const [controlledPlaceholder, setControlledPlaceholder] = useState<string>(initialPlaceholder);
    const {
        dataToDisplay,
        handleLoadMore,
        handleSearchChange,
        hasNextPage,
        isLoading,
        isError,
        isOpen,
        isOpenPending,
        actionsRef,
        setOpen,
        dropPendingOpen,
        onInputKeyDown,
    } = usePaginatedPopup<TOption>(paginated);
    const fallbackTranslation = defaultGroup ?? t('field.other');
    const { getStartAdornment, getEndAdornment } = useAdornmentGetters(getOptionStartAdornment, getOptionEndAdornment);
    // ponytail: LAST_NAME_FIRST flips any option carrying first+last (person/employee) to "Last First".
    const lastNameFirst = useLastNameFirst();

    const chipsAnchorRef = useComboboxAnchor();
    const listRootWrapperRef = useRef<HTMLDivElement | null>(null);
    const [loaderEl, setLoaderEl] = useState<HTMLDivElement | null>(null);
    useInfiniteScroll(
        loaderEl,
        {
            observer: { root: listRootWrapperRef.current, rootMargin: '75px', threshold: 0 },
            disabled: isError,
            isLoading,
        },
        handleLoadMore
    );

    const getLabel = (option: TOption | null) => {
        if (!option) {
            return null;
        }

        if (typeof getOptionLabel === 'string') {
            return optionDisplayLabel(option, lastNameFirst, get(option, getOptionLabel));
        } else {
            return getOptionLabel(option);
        }
    };

    const getValue = (option: TOption | null) => {
        if (!option) {
            return null;
        }

        if (typeof getOptionIdentifier === 'string') {
            return get(option, getOptionIdentifier);
        } else {
            return getOptionIdentifier(option);
        }
    };

    const getGroupedBy = (option: TOption | null): string => {
        if (!option) {
            return fallbackTranslation;
        }

        if (groupByStaticFields) {
            return groupByStaticFields(option) ?? fallbackTranslation;
        }

        let groupValue: string | number | null | undefined;

        if (typeof groupByField === 'string') {
            groupValue = get(option, groupByField);
        } else if (typeof groupByField === 'function') {
            groupValue = groupByField(option);
        } else {
            return fallbackTranslation;
        }

        return groupValue != null ? String(groupValue) : fallbackTranslation;
    };

    // Unless `keepSelected`, already-picked options are taken out of the list rather than shown ticked: the
    // chips above the input are what says they are selected.
    const selectableOptions = useMemo(() => {
        if (keepSelected) {
            return dataToDisplay;
        }
        const selectedIds = new Set(((watchedValue ?? []) as TOption[]).map((option) => getValue(option)));
        return dataToDisplay.filter((option) => !selectedIds.has(getValue(option)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataToDisplay, watchedValue, getOptionIdentifier, keepSelected]);
    const selectedRowClass = keepSelected ? 'new:data-selected:bg-primary-wash' : undefined;

    const isGrouped = !!(groupByStaticFields || groupByField);
    const groupedOptions = useMemo<OptionGroup<TOption>[]>(() => {
        if (!isGrouped) {
            return [];
        }
        return groupConsecutiveOptions(selectableOptions, getGroupedBy);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectableOptions, isGrouped, groupByField, groupByStaticFields, fallbackTranslation]);

    const getSameNameEmail = useSameNameEmail(dataToDisplay);

    const renderOptionRow = (option: TOption) => {
        if (customOptionRenderer) {
            const { element, isDisabled } = customOptionRenderer(option, {}, getValue);
            return (
                <ComboboxItem key={getValue(option)} value={option} disabled={isDisabled} className={selectedRowClass}>
                    {element}
                </ComboboxItem>
            );
        }

        return (
            <ComboboxItem
                key={getValue(option)}
                value={option}
                title={getLabel(option) || undefined}
                className={selectedRowClass}
            >
                <div className="flex w-full min-w-0 items-center">
                    {getOptionStartAdornment ? (
                        <span className="mr-[.5ch] flex items-center">{getStartAdornment(option)}</span>
                    ) : null}
                    <span className="min-w-0 flex-1 truncate">
                        <PersonOptionLabel
                            option={option}
                            label={getLabel(option)}
                            sameNameEmail={getSameNameEmail(option)}
                        />
                    </span>
                    {getOptionEndAdornment ? (
                        <span className="ml-[.5ch] flex items-center">{getEndAdornment(option)}</span>
                    ) : null}
                </div>
            </ComboboxItem>
        );
    };

    return (
        <Controller
            control={control}
            name={name}
            rules={rules}
            render={({ field: { ref, onChange: innerOnChange, onBlur, value }, fieldState: { error } }) => {
                const selected = (value ?? []) as TOption[];

                const commit = (
                    next: TOption[],
                    reason: MultiSelectChangeReason,
                    details?: MultiSelectChangeDetails<TOption>
                ) => {
                    innerOnChange(next);
                    if (onChange) {
                        onChange(next, reason, details);
                    }
                };

                const getTagProps: GetTagProps = ({ index }) => ({
                    key: getValue(selected[index]) ?? index,
                    disabled,
                    section: inSection,
                    onDelete: () =>
                        commit(
                            selected.filter((_, position) => position !== index),
                            'removeOption',
                            { option: selected[index] }
                        ),
                });

                return (
                    <InputWrapper
                        name={name}
                        label={label}
                        shouldTranslateLabel={shouldTranslateLabel}
                        tooltip={tooltip}
                        required={required}
                        isEmpty={isEmpty}
                        readonly={!!readonly}
                        rightLabelComponent={rightLabelComponent}
                        bottomRightLabelComponent={bottomRightLabelComponent}
                        hasErrors={!!error}
                        error={readonly ? undefined : error}
                        className={cn(wrapperClassName, inSection && SECTION_MULTI_SELECT_CLASS, wideClass)}
                    >
                        {readonly ? (
                            <ReadonlyField
                                value={value}
                                getLabel={getLabel}
                                getValue={getValue}
                                linkTo={typeof readonly === 'object' ? readonly?.linkTo : undefined}
                            />
                        ) : (
                            <Combobox<TOption, true>
                                multiple
                                items={(isGrouped ? groupedOptions : selectableOptions) as readonly TOption[]}
                                value={selected}
                                disabled={disabled}
                                // Search runs on the server, so the popup shows exactly the page it was handed.
                                filter={null}
                                itemToStringLabel={(option) => String(getLabel(option) ?? '')}
                                isItemEqualToValue={(option, selectedOption) =>
                                    getValue(option) === getValue(selectedOption)
                                }
                                open={isOpen}
                                onOpenChange={setOpen}
                                actionsRef={actionsRef}
                                onValueChange={(next, details) => {
                                    const isRemoval = next.length < selected.length;
                                    // The option that moved is found by diffing the two lists, never
                                    // by position: a chip's own Backspace/Delete takes the value out
                                    // at that chip's index, so neither list's last entry names it.
                                    const [from, to] = isRemoval ? [selected, next] : [next, selected];
                                    const changed = from.find(
                                        (option) => !to.some((other) => getValue(other) === getValue(option))
                                    );
                                    commit(
                                        next,
                                        isRemoval
                                            ? details.reason === 'clear-press'
                                                ? 'clear'
                                                : 'removeOption'
                                            : 'selectOption',
                                        { option: changed } as MultiSelectChangeDetails<TOption>
                                    );
                                }}
                                onInputValueChange={(data, details) => {
                                    // Only what the user typed drives a refetch; the input is also
                                    // rewritten when an item is picked or the field loses focus, and
                                    // searching for the value just chosen would throw the list away.
                                    if (details.reason !== 'input-change' && details.reason !== 'input-clear') {
                                        if (details.reason === 'focus-out') {
                                            onSearchChange?.('blur', data);
                                        }
                                        return;
                                    }
                                    handleSearchChange(data);
                                    onSearchChange?.(details.event?.type ?? details.reason, data);
                                }}
                            >
                                {/* The chips row is the anchor, so the popup lines up with the whole
                                    field rather than with the text caret inside it. Its `min-h-9` is
                                    what keeps an empty field and a one-chip field the same height. */}
                                <ComboboxChips
                                    ref={chipsAnchorRef}
                                    // The kit tightens this padding once a chip is present; holding it
                                    // steady keeps the caret from stepping sideways on the first pick.
                                    className="bg-paper has-data-[slot=combobox-chip]:px-2.5"
                                >
                                    {renderTags
                                        ? renderTags(selected, getTagProps)
                                        : selected.map((option, index) => (
                                              // The chip is what registers into the composite list the
                                              // input's arrow keys walk; its own pill styling is dropped
                                              // because the tag inside already draws one.
                                              <ComboboxChip
                                                  key={getValue(option) ?? index}
                                                  showRemove={false}
                                                  className="h-auto max-w-full bg-transparent p-0"
                                              >
                                                  <AutocompleteTag
                                                      section={inSection}
                                                      icon={getSelectedValueStartAdornment?.(option)}
                                                      adornment={getSelectedValueEndAdornment?.(option)}
                                                      label={getLabel(option)}
                                                      title={getLabel(option) || undefined}
                                                      disabled={disabled}
                                                      onDelete={getTagProps({ index }).onDelete}
                                                  />
                                              </ComboboxChip>
                                          ))}
                                    <ComboboxChipsInput
                                        id={name}
                                        name={name}
                                        ref={ref}
                                        className={inSection ? SECTION_CHIPS_INPUT_CLASS : undefined}
                                        aria-invalid={!!error}
                                        aria-busy={isOpenPending || undefined}
                                        required={required ?? false}
                                        disabled={disabled}
                                        placeholder={controlledPlaceholder}
                                        onKeyDown={onInputKeyDown}
                                        onFocus={() => {
                                            if (placeholderOnFocus) {
                                                setControlledPlaceholder(t(placeholderOnFocus));
                                            }
                                        }}
                                        onBlur={() => {
                                            onBlur();
                                            dropPendingOpen();
                                            if (placeholderOnFocus) {
                                                setControlledPlaceholder(initialPlaceholder);
                                            }
                                        }}
                                    />
                                    {/* Only mounts once something is selected; `clear-press` is the
                                        reason the change handler already maps to `clear`. Sized to
                                        the input's own line so it never out-grows the row's `min-h-9`:
                                        the kit's 24px button would add 2px on the first pick. */}
                                    {clearable !== false ? <ComboboxClear className="size-5" /> : null}
                                    {/* The chevron every dropdown shows, at the clear button's size and,
                                        like the single-value fields', with no Tab stop. While the first
                                        page loads a spinner takes its slot, so the row cannot wrap. */}
                                    {isOpenPending ? (
                                        <CircularLoader className="static -mr-1 size-5 [&>span]:size-4" />
                                    ) : (
                                        <InputGroupButton
                                            size="icon-xs"
                                            variant="ghost"
                                            render={<ComboboxTrigger />}
                                            tabIndex={-1}
                                            className="-mr-1 size-5 data-pressed:bg-transparent"
                                            disabled={disabled}
                                        />
                                    )}
                                </ComboboxChips>
                                <ComboboxContent anchor={chipsAnchorRef} className="shadow-dropdown">
                                    <ComboboxEmpty>
                                        {isLoading ? t('loading') : (noOptionsContent ?? t('no-options'))}
                                    </ComboboxEmpty>
                                    {/* Mapped rather than handed to the list's function child: filtering
                                        is off here, and the load-more row has to sit inside the scroll
                                        box, which the function form leaves no room for. */}
                                    <ComboboxList ref={listRootWrapperRef} className={listClassName}>
                                        {isGrouped
                                            ? groupedOptions.map((group) => (
                                                  <ComboboxGroup key={group.key} items={group.items}>
                                                      <ComboboxLabel>{group.value}</ComboboxLabel>
                                                      <ComboboxCollection>
                                                          {(option: TOption) => renderOptionRow(option)}
                                                      </ComboboxCollection>
                                                  </ComboboxGroup>
                                              ))
                                            : selectableOptions.map((option) => renderOptionRow(option))}
                                        {hasNextPage ? (
                                            <div ref={setLoaderEl} className="relative py-2">
                                                <CircularLoader className="static [&>span]:size-6" />
                                            </div>
                                        ) : null}
                                    </ComboboxList>
                                </ComboboxContent>
                            </Combobox>
                        )}
                    </InputWrapper>
                );
            }}
        />
    );
};

export default DynamicMultiSelectInput;
